import type {
    AssistantConfigStatus,
    AssistantResponse,
    OpenAiAssistantRequest
} from './types';
import { supabase } from '../../lib/supabaseClient';

const getEnvValue = (key: string): string | undefined => {
    const envRecord = (import.meta as ImportMeta & {
        env?: Record<string, string | undefined>;
    }).env ?? {};
    const value = envRecord[key];
    if (typeof value === 'string') {
        return value.trim();
    }
    return undefined;
};

const getSupabaseUrl = (): string => {
    return getEnvValue('VITE_SUPABASE_URL') || 'http://localhost:54321';
};

const getSupabaseAnonKey = (): string => {
    return getEnvValue('VITE_SUPABASE_ANON_KEY') || '';
};

const resolveAssistantUrl = (): string => {
    const supabaseUrl = getSupabaseUrl();
    return `${supabaseUrl}/functions/v1/assistant`;
};

export const getAssistantConfigStatus = (): AssistantConfigStatus => {
    const supabaseUrl = getSupabaseUrl();
    const missing: string[] = [];

    if (!supabaseUrl || supabaseUrl === 'http://localhost:54321') {
        const configured = getEnvValue('VITE_SUPABASE_URL');
        if (!configured) {
            missing.push('VITE_SUPABASE_URL');
        }
    }

    if (!getSupabaseAnonKey()) {
        missing.push('VITE_SUPABASE_ANON_KEY');
    }

    return {
        isConfigured: missing.length === 0,
        missing
    };
};

export const callOpenAiAssistant = async <T = unknown>(
    request: OpenAiAssistantRequest
): Promise<AssistantResponse<T>> => {
    // Get current session token for authentication
    const { data: { session: initialSession } } = await supabase.auth.getSession();

    if (!initialSession?.access_token) {
        throw new Error('Not authenticated. Please sign in to use the assistant.');
    }

    const supabaseAnonKey = getSupabaseAnonKey();
    if (!supabaseAnonKey) {
        throw new Error('Supabase anon key not configured. Set VITE_SUPABASE_ANON_KEY to use the assistant.');
    }

    const url = resolveAssistantUrl();
    const body = JSON.stringify({
        messages: request.messages,
        temperature: request.temperature,
        maxOutputTokens: request.maxOutputTokens,
        simulationId: request.simulationId,
        graphSnapshot: request.graphSnapshot
    });

    const sendRequest = (accessToken: string) => fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'apikey': supabaseAnonKey
        },
        signal: request.signal,
        body
    });

    let res = await sendRequest(initialSession.access_token);
    if (res.status === 401) {
        const { data: { session: refreshedSession } } = await supabase.auth.refreshSession();
        if (refreshedSession?.access_token && refreshedSession.access_token !== initialSession.access_token) {
            res = await sendRequest(refreshedSession.access_token);
        }
    }

    if (!res.ok) {
        let reason = res.statusText;
        try {
            const data = (await res.json()) as { error?: string; message?: string };
            if (data?.message) {
                reason = data.message;
            } else if (data?.error) {
                reason = data.error;
            }
        } catch (error) {
            // Ignore JSON parse errors for non-JSON responses
        }

        throw new Error(`Assistant request failed (${res.status}): ${reason}`);
    }

    const data = (await res.json()) as AssistantResponse<T>;

    if (!data || typeof data.content !== 'string') {
        throw new Error('Assistant response missing content.');
    }

    return data;
};
